import React from 'react';
import { Search, Sparkles, MessageCircle, ShieldCheck, BookOpen, Headphones, FileText } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const helpTopics = [
  {
    title: 'Getting Started',
    description: 'Create your first child profile, customize avatars, and sync devices.',
    icon: <Sparkles className="h-5 w-5 text-purple-600" />,
    links: ['Creating profiles', 'Importing photos', 'Linking co-parents'],
  },
  {
    title: 'Stories & Content',
    description: 'Manage adventures, saved drafts, and offline downloads.',
    icon: <BookOpen className="h-5 w-5 text-pink-600" />,
    links: ['Downloading stories', 'Audio narration', 'Illustration packs'],
  },
  {
    title: 'Privacy & Security',
    description: 'Learn about parental controls, compliance, and data exports.',
    icon: <ShieldCheck className="h-5 w-5 text-emerald-600" />,
    links: ['Requesting deletion', 'Classroom mode', 'Two-factor tips'],
  },
];

const faqs = [
  {
    question: 'Can I use MyStoryMe without installing the PWA?',
    answer:
      'Absolutely. The web experience mirrors the PWA desktop preview and supports mobile browsers. Installing unlocks offline mode and push alerts.',
  },
  {
    question: 'How many child profiles can I create?',
    answer: 'Family plans include 5 active heroes. You can archive older profiles or upgrade for more seats.',
  },
  {
    question: 'Do you offer educator or library pricing?',
    answer:
      'Yes. Visit the Feedback page to tell us about your program or email classrooms@mystoryme.com for tailored bundles.',
  },
];

const HelpCenter = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 lg:py-20">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-purple-700 font-semibold shadow">
              <Headphones className="h-4 w-4" />
              Help Center
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-6 mb-4">Need backup? We’ve got you.</h1>
            <p className="text-lg text-gray-600">
              Search our library of guides inspired by the MyStoryMe PWA desktop flow—complete with gradient cards,
              accessibility tips, and parent-first controls.
            </p>
          </div>

          <div className="max-w-2xl mx-auto mb-16">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400" />
              <input
                type="text"
                placeholder="Search topics, e.g. “offline downloads”"
                className="w-full rounded-2xl border border-purple-100 bg-white/90 px-12 py-4 text-lg shadow-lg focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {helpTopics.map((topic) => (
              <div
                key={topic.title}
                className="rounded-3xl border border-purple-100 bg-white p-6 shadow-xl hover:shadow-2xl transition-shadow"
              >
                <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center mb-4">{topic.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{topic.title}</h3>
                <p className="text-gray-600 mb-4">{topic.description}</p>
                <ul className="space-y-1 text-sm text-purple-600 font-medium">
                  {topic.links.map((link) => (
                    <li key={link}>• {link}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
            <div className="bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-3xl p-8 shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <MessageCircle className="h-6 w-6" />
                <h2 className="text-2xl font-bold">Still need help?</h2>
              </div>
              <p className="text-white/90 mb-6">
                Chat with Story Concierge or drop us a line. Our average response time is under 30 minutes during business
                hours.
              </p>
              <div className="space-y-3 text-sm">
                <p>Email: support@mystoryme.com</p>
                <p>Live chat: 8am – 8pm EST</p>
                <p>Emergency hotline: +1 (800) 555-1234</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-purple-100 p-8 shadow-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Featured FAQs</h3>
              <div className="space-y-4">
                {faqs.map((faq) => (
                  <details key={faq.question} className="border border-purple-50 rounded-2xl p-4 bg-purple-50/30">
                    <summary className="font-semibold text-gray-900 cursor-pointer">{faq.question}</summary>
                    <p className="text-gray-600 mt-2">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-purple-100 bg-white p-8 shadow-xl text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 text-purple-700 font-semibold mb-4">
              <FileText className="h-4 w-4" />
              Documentation
            </div>
            <p className="text-lg text-gray-600 mb-6">
              Need onboarding decks or compliance PDFs? Visit the Privacy page or request tailored guides via Feedback.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/privacy"
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold shadow-lg"
              >
                Review privacy policy
              </a>
              <a
                href="/feedback"
                className="px-6 py-3 rounded-xl border border-purple-200 text-purple-700 font-semibold bg-white"
              >
                Submit a request
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HelpCenter;


